import { RelativePeriod } from '@components/comparison-period-selector/Types';
import getComparisonPeriod, { PeriodFormat } from '@components/filter-selection-note/GetComparisonPeriod';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { useToastMessage } from '@components/toast-message/ToastMessage';
import { SaleTypeMappingProducts } from '@const/PerformanceType';
import { OnExportReadyCallback, useChartExport } from '@ecosystem/export-utils';
import { ExportFormat } from '@ecosystem/newron-client-lib';
import { useExportTable } from '@hooks/export/export-table/UseExportTable';
import { getExportTrackingFormat } from '@hooks/export/export-tracking/ExportTracking';
import { displayExportErrorToastMessage, getAppliedFiltersText } from '@hooks/export/ExportUtils';
import {
  getBarChartKpiDisplayName,
  LeaderboardKpi,
  ProductAnalyzerGroupKpi,
  ProductsPage,
  ProductsParam,
  SaleType,
} from '@models/Products';
import { RI_PRODUCT_LEADERBOARD_CARD_TITLE } from '@pages/Utilities';
import { BaseTimePeriod } from '@services/FiltersTypes';
import { getExportTopSellingProducts } from '@services/ProductsLeaderboard.service';
import { ElementType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import {
  dataExportedTrackingFunction,
  DataExportTrackingElementParam,
  useDataExportTracking,
} from '@utils/page-analytics/model/DataExportTracking';
import { PagesConfig } from '@utils/pageConfig/PagesConfigContext';
import { FactRevenue, Filters, Option, PageName, PageType } from '@utils/pageConfig/Types';
import { formatProductsPeriod } from '@utils/PeriodUtils';
import { TOTAL } from '@utils/TotalUtils';
import { useExportContext } from '@hooks/export/context/ExportContext';
import {
  ExportDoubleBarChartOptions,
  useExportDoubleBarChart,
} from '@hooks/export/export-double-bar-chart/hooks/UseExportDoubleBarChart';

export interface UseTopSellingExportExcelParam {
  productsParam?: ProductsParam;
  mainFilterStateProducts: any;
  leaderboardContent: LeaderboardKpi[];
  pagesConfig: PagesConfig;
  appliedCountries: Option[];
  appliedPg?: Option[];
  factRevenue: FactRevenue;
  timeFrame?: Periodicity;
  comparisonPeriod: RelativePeriod | BaseTimePeriod;
  leaderboardUpdate?: boolean;
  isTableView: boolean;
  saleType: SaleType;
  sortBy: string;
  sortDirection: string;
  sortKey: string | undefined;
  selectedKpi: Option;
}

export const getGroupedDataRowsForExport = (response: ProductsPage): LeaderboardKpi[] => {
  return (response.groupingKpi ?? []).reduce<LeaderboardKpi[]>((acc, group) => {
    acc.push(...group.groupKpis.filter((groupKpi) => groupKpi.item.itemName !== TOTAL));
    return acc;
  }, []);
};

const constructLeaderboardKpiForGroupingExcel = (grouping: string): LeaderboardKpi => {
  return {
    item: { itemId: null, itemName: 'TOTAL', itemBrand: null, firstActivityDate: null },
    marketRank: { rank: null, change: null, position: null },
    marketRevenueRank: { rank: null, change: null, position: null },
    marketVolumeRank: { rank: null, change: null, position: null },
    marketRevenue: { value: null, actual: null, percentage: null },
    marketVolume: { value: null, actual: null, percentage: null },
    marketPrice: { value: null, actual: null, percentage: null },
    marketShareRevenue: { growthPercentage: null, sharePercentage: null },
    marketShareVolume: { growthPercentage: null, sharePercentage: null },
    marketShareRatio: null,
    marketShareValue: null,
    retailerRank: { rank: null, change: null, position: null },
    retailerRevenueRank: { rank: null, change: null, position: null },
    retailerVolumeRank: { rank: null, change: null, position: null },
    retailerRevenue: { value: null, actual: null, percentage: null },
    retailerVolume: { value: null, actual: null, percentage: null },
    retailerPrice: { value: null, actual: null, percentage: null },
    retailerShareRevenue: { growthPercentage: null, sharePercentage: null },
    retailerShareVolume: { growthPercentage: null, sharePercentage: null },
    retailerMixRevenue: { growthPercentage: null, sharePercentage: null },
    retailerMixVolume: { growthPercentage: null, sharePercentage: null },
    marketExcludeRetailRevenue: { value: null, actual: null, percentage: null },
    marketExcludeRetailVolume: { value: null, actual: null, percentage: null },
    marketExcludeRetailPrice: { value: null, actual: null, percentage: null },
    grouping,
  } as unknown as LeaderboardKpi;
};

export const getSummaryDataRowsForExcel = (dataForExport: ProductsPage): LeaderboardKpi[] => {
  const groupsWithEmptyKpis: ProductAnalyzerGroupKpi[] = (dataForExport.groupingKpi ?? []).filter((group) =>
    group.groupKpis.length === 0
      ? group.groupKpis.push(constructLeaderboardKpiForGroupingExcel(group.groupDetails.groupName))
      : group.groupKpis,
  );

  // Edge case when groupName is equal to itemName
  const groupsTotal: LeaderboardKpi[] = [];
  for (const group of groupsWithEmptyKpis) {
    const groupTotal = group.groupKpis.find((pgKpis) => pgKpis.item.itemName === 'TOTAL')!;
    groupTotal.item.itemName = group.groupDetails.groupName;
    groupsTotal.push(groupTotal);
  }

  return groupsTotal;
};

type UseTopSellingExportExcelCallbackReturn = {
  onChartDownload: (exportFormats: ExportFormat[]) => Promise<void>;
  onExportReady: OnExportReadyCallback;
};

export const useTopSellingExportExcelCallback = (
  param: UseTopSellingExportExcelParam,
  prodLeaderboardFeatureProductFilters: Filters[],
): UseTopSellingExportExcelCallbackReturn => {
  const {
    productsParam,
    leaderboardContent,
    pagesConfig,
    appliedCountries,
    appliedPg,
    factRevenue,
    comparisonPeriod,
    isTableView,
    saleType,
    sortBy,
    sortDirection,
    sortKey,
    selectedKpi,
  } = param;

  const [invokeExport, onExportReady] = useChartExport();
  const { multipleExportCallbacks } = useExportContext();
  const { download: exportTable } = useExportTable(prodLeaderboardFeatureProductFilters);
  const { download: exportDoubleBarChart } = useExportDoubleBarChart();

  const { showToastMessage } = useToastMessage();
  //Amplitude tracking
  const handleDataExportTrackingFunction = useDataExportTracking('mainFiltersProducts', 'hitlistsRetailerClient');

  const handleExportedTracking = (exportFormats: ExportFormat[]) => {
    const elementKpi = isTableView
      ? SaleTypeMappingProducts[saleType]
      : getBarChartKpiDisplayName(pagesConfig.sortingOptionsProducts.headerKey);
    const dataExportedTrackingParam: DataExportTrackingElementParam = {
      elementType: isTableView ? ElementType.TABLE : ElementType.BAR_CHART,
      elementKpi: elementKpi,
      elementSort: pagesConfig.sortingOptionsProducts,
      optionChosen: exportFormats.map((format) => getExportTrackingFormat(format)),
    };
    handleDataExportTrackingFunction(dataExportedTrackingParam, dataExportedTrackingFunction);
  };

  const onChartDownload = async (exportFormats: ExportFormat[]): Promise<void> => {
    if (!productsParam) {
      return;
    }
    const timePeriod = productsParam.period;
    const periodType = productsParam.periodType;
    const comparisonTimePeriod = getComparisonPeriod(
      {
        label: productsParam.period,
        value: productsParam.period,
        lastUpdatedDate: 0,
      },
      periodType,
      comparisonPeriod,
      PeriodFormat.LONG,
    );
    if (leaderboardContent.length <= 0) {
      return;
    }

    try {
      const exportProductParam = {
        ...productsParam,
        sortBy: param.sortBy,
        sortDirection: param.sortDirection,
        sortKey: param.sortKey,
      };

      const dataForExport: ProductsPage = await getExportTopSellingProducts(exportProductParam);
      const groupedData: LeaderboardKpi[] = getGroupedDataRowsForExport(dataForExport);
      const summaryData: LeaderboardKpi[] = getSummaryDataRowsForExcel(dataForExport);
      let data: LeaderboardKpi[] = dataForExport.content;
      // TOTAL Row should stick at first position followed by groups and rest all Product rows
      const totalRow = data.find((product) => product?.item?.itemName === 'TOTAL');
      data = data.filter((product) => product?.item?.itemName !== 'TOTAL');
      if (totalRow) {
        summaryData.unshift(totalRow);
      }

      await exportTable({
        exportFormats,
        invokeExport,
        title: RI_PRODUCT_LEADERBOARD_CARD_TITLE,
        appliedCountry: appliedCountries,
        appliedPg: appliedPg,
        data: [...groupedData, ...data],
        summaryData,
        timePeriod: formatProductsPeriod(timePeriod, periodType, true),
        comparisonTimePeriod: formatProductsPeriod(comparisonTimePeriod, productsParam.periodType, true),
        factRevenue,
        periodType,
        showPerformanceWithin: '',
        appliedFiltersText: getAppliedFiltersText(pagesConfig, PageType.RI_PRODUCT),
        selectedKpi: selectedKpi,
        pageName: PageName.RI_PRODUCT,
        pagesConfig: pagesConfig,
        sortBy: sortBy,
        sortDirection: sortDirection,
        sortKey: sortKey,
      });

      const options: ExportDoubleBarChartOptions = {
        exportFormats: exportFormats.filter((e) => e !== ExportFormat.Xlsx),
        invokeExport: invokeExport,
        title: RI_PRODUCT_LEADERBOARD_CARD_TITLE,
        appliedCountry: appliedCountries,
        appliedProductGroup: appliedPg,
        data: [...groupedData, ...data],
        period: timePeriod,
        comparisonTimePeriod,
        factRevenue: factRevenue,
        periodType: periodType,
        appliedFiltersText: getAppliedFiltersText(pagesConfig, PageType.RI_PRODUCT),
        selectedKpi: selectedKpi,
        multipleChartsExport: multipleExportCallbacks,
        pageName: PageName.RI_PRODUCT,
        showPerformanceWithin: '',
        summaryData,
      };

      await exportDoubleBarChart(options);

      handleExportedTracking(exportFormats);
    } catch (e: any) {
      displayExportErrorToastMessage(
        e,
        'Top Selling Products - exportTableAndBarChart error.',
        productsParam,
        showToastMessage,
      );
      return;
    }
  };

  return { onChartDownload, onExportReady };
};
